import React, { useState } from 'react';
import { X, User, Phone, MapPin, Package, Calendar, Truck, Clock, AlertTriangle, CheckCircle, Archive, MessageSquare, ShoppingCart } from 'lucide-react';

const STATUS_CONFIG = {
  pending: { label: 'En attente', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  scheduled: { label: 'Planifié', color: 'bg-blue-100 text-blue-800', icon: Calendar },
  assigned: { label: 'Affecté', color: 'bg-purple-100 text-purple-800', icon: Truck },
  in_progress: { label: 'En cours', color: 'bg-orange-100 text-orange-800', icon: Truck },
  collected: { label: 'Déposé', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  cancelled: { label: 'Annulé', color: 'bg-red-100 text-red-800', icon: AlertTriangle },
  archived: { label: 'Archivé', color: 'bg-gray-100 text-gray-600', icon: Archive },
};

const BARREL_LABELS = {
  noir_270l: 'Barrique Noire 270L',
  bleu_220l: 'Barrique Bleue 220L',
};

const PRIORITY_CONFIG = {
  low: { label: 'Basse', color: 'bg-gray-100 text-gray-700' },
  normal: { label: 'Normale', color: 'bg-blue-100 text-blue-700' },
  high: { label: 'Haute', color: 'bg-orange-100 text-orange-700' },
  urgent: { label: 'Urgente', color: 'bg-red-100 text-red-700' },
};

export default function DepotDetailModal({ deposit, drivers, onClose }) {
  if (!deposit) return null;

  const status = deposit?.collection_status || 'pending';
  const statusCfg = STATUS_CONFIG?.[status] || STATUS_CONFIG?.pending;
  const priority = deposit?.priority || 'normal';
  const priorityCfg = PRIORITY_CONFIG?.[priority] || PRIORITY_CONFIG?.normal;
  const driver = drivers?.find(d => d?.id === deposit?.assigned_driver_id);

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr)?.toLocaleDateString('fr-FR', {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr)?.toLocaleString('fr-FR');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <Package className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Fiche Dépôt</h2>
              <p className="text-sm text-gray-500">#{deposit?.id?.slice(0, 8)?.toUpperCase()}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Status + Priority */}
          <div className="flex flex-wrap gap-3">
            <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold ${statusCfg?.color}`}>
              <statusCfg.icon className="w-4 h-4" />
              {statusCfg?.label}
            </span>
            <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold ${priorityCfg?.color}`}>
              Priorité : {priorityCfg?.label}
            </span>
            {deposit?.is_archived && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold bg-gray-100 text-gray-600">
                <Archive className="w-4 h-4" />
                Archivé
              </span>
            )}
          </div>

          {/* Linked order */}
          {deposit?.sale_id && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center gap-2">
              <ShoppingCart className="w-4 h-4 text-blue-600 flex-shrink-0" />
              <span className="text-sm text-blue-800">
                Lié à la commande client <strong>#{deposit?.sale_id?.slice(0, 8)?.toUpperCase()}</strong>
              </span>
            </div>
          )}

          {/* Client Info */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Informations Client</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <div>
                  <div className="text-xs text-gray-500">Client</div>
                  <div className="text-sm font-medium text-gray-900">{deposit?.client_name || '—'}</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <div>
                  <div className="text-xs text-gray-500">Téléphone</div>
                  <div className="text-sm font-medium text-gray-900">{deposit?.client_phone || '—'}</div>
                </div>
              </div>
              <div className="flex items-start gap-2 sm:col-span-2">
                <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="text-xs text-gray-500">Adresse</div>
                  <div className="text-sm font-medium text-gray-900">
                    {deposit?.client_address}{deposit?.client_city ? `, ${deposit?.client_city}` : ''}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Articles & Quantities */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Articles & Quantités</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <div className="text-xs text-gray-500">Article</div>
                <div className="text-sm font-medium text-gray-900">{BARREL_LABELS?.[deposit?.barrel_type] || deposit?.barrel_type || '—'}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Quantité</div>
                <div className="text-sm font-medium text-gray-900">{deposit?.quantity || '—'}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Prix total</div>
                <div className="text-sm font-medium text-green-700">{deposit?.total_price ? `${parseFloat(deposit?.total_price)?.toFixed(2)} €` : '—'}</div>
              </div>
            </div>
          </div>

          {/* Dates */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Dates</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="flex items-start gap-2">
                <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="text-xs text-gray-500">Date de création</div>
                  <div className="text-sm font-medium text-gray-900">{formatDateTime(deposit?.created_at)}</div>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="text-xs text-gray-500">Date prévue</div>
                  <div className="text-sm font-medium text-gray-900">{formatDate(deposit?.planned_collection_date)}</div>
                </div>
              </div>
              {deposit?.actual_collection_date && (
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="text-xs text-gray-500">Date réelle de dépôt</div>
                    <div className="text-sm font-medium text-gray-900">{formatDate(deposit?.actual_collection_date)}</div>
                  </div>
                </div>
              )}
              {deposit?.time_slot && (
                <div className="flex items-start gap-2">
                  <Clock className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="text-xs text-gray-500">Tranche horaire</div>
                    <div className="text-sm font-medium text-gray-900">{deposit?.time_slot}</div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Driver */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Chauffeur Affecté</h3>
            <div className="flex items-center gap-2">
              <Truck className="w-4 h-4 text-gray-400" />
              <span className="text-sm font-medium text-gray-900">
                {driver?.name || deposit?.employees?.name || 'Non assigné'}
              </span>
            </div>
          </div>

          {/* Cancellation info */}
          {(deposit?.is_cancelled || deposit?.collection_status === 'cancelled') && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-red-700 uppercase tracking-wide mb-2 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Annulation
              </h3>
              {deposit?.cancelled_at && (
                <div className="text-sm text-red-800 mb-1">
                  Annulé le : <strong>{formatDateTime(deposit?.cancelled_at)}</strong>
                </div>
              )}
              {deposit?.cancelled_reason && (
                <div className="text-sm text-red-800">
                  Motif : {deposit?.cancelled_reason}
                </div>
              )}
            </div>
          )}

          {/* Archive info */}
          {deposit?.is_archived && deposit?.archived_at && (
            <div className="bg-gray-100 border border-gray-300 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-2 flex items-center gap-2">
                <Archive className="w-4 h-4" />
                Archivage
              </h3>
              <div className="text-sm text-gray-700">
                Archivé le : <strong>{formatDateTime(deposit?.archived_at)}</strong>
              </div>
            </div>
          )}

          {/* Notes */}
          {deposit?.delivery_notes && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-yellow-700 uppercase tracking-wide mb-2 flex items-center gap-2">
                <MessageSquare className="w-4 h-4" />
                Notes de livraison
              </h3>
              <p className="text-sm text-gray-800">{deposit?.delivery_notes}</p>
            </div>
          )}

          {deposit?.admin_comment && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-blue-700 uppercase tracking-wide mb-2 flex items-center gap-2">
                <MessageSquare className="w-4 h-4" />
                Commentaire admin
              </h3>
              <p className="text-sm text-gray-800">{deposit?.admin_comment}</p>
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 rounded-b-xl">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}
